import type { LeadStatus } from '@up2stay/up2promo-api-client';
import { LeadContactChannel } from '@up2stay/up2promo-api-client';
import { Alert, AlertDescription, Button } from '@up2stay/up2promo-ui';
import { Card, CardContent } from '@up2stay/up2promo-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@up2stay/up2promo-ui';
import { Skeleton } from '@up2stay/up2promo-ui';
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  Ellipsis,
  Mail,
  MessageSquare,
  Phone,
  Trash2,
  Users,
} from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AppPageContent } from '@/core/components/app-page-content';
import { isLeadArchived } from '@/core/lib/lead-mutate-access';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { UpgradeCard } from '@/core/components/billing/upgrade-surfaces';
import { LeadDetailAttachments } from '@/core/components/leads/lead-detail/lead-detail-attachments';
import { LeadDetailDeleteDialog } from '@/core/components/leads/lead-detail/lead-detail-delete-dialog';
import { LeadDetailDescription } from '@/core/components/leads/lead-detail/lead-detail-description';
import { LeadDetailEvents } from '@/core/components/leads/lead-detail/lead-detail-events';
import {
  buildLeadDetailAttachmentLabels,
  buildLeadDetailPropertyLabels,
  buildLeadDetailTimelineLabels,
} from '@/core/components/leads/lead-detail/lead-detail-labels';
import {
  LeadDetailMobile,
  LeadDetailMobileOverflowMenu,
} from '@/core/components/leads/lead-detail/lead-detail-mobile';
import { LeadDetailNameTitle } from '@/core/components/leads/lead-detail/lead-detail-name-title';
import { type ContactFormField } from '@/core/components/leads/lead-detail/lead-detail-properties';
import { LeadDetailSectionCard } from '@/core/components/leads/lead-detail/lead-detail-section-card';
import { LeadDetailSidebar } from '@/core/components/leads/lead-detail/lead-detail-sidebar';
import { LeadDetailTags } from '@/core/components/leads/lead-detail/lead-detail-tags';
import { LeadDetailTimeline } from '@/core/components/leads/lead-detail/lead-detail-timeline';
import { LeadSaveToDeviceMenu } from '@/core/components/leads/lead-detail/lead-save-to-device-menu';
import { LeadStatusSelect } from '@/core/components/leads/lead-detail/lead-status-badge';
import { LeadAssigneePicker } from '@/core/components/leads/lead-assignee-picker';
import { leadAssigneePickerLabelsFromDict } from '@/core/components/leads/lead-assignee-picker-labels';
import {
  formatLeadCapturedAt,
  leadCompanyLine,
  leadInitials,
} from '@/core/components/leads/leads-list-display';
import { ListEmptyState } from '@/core/components/layout';
import {
  PageHeader,
  PAGE_HEADER_CHROME_BUTTON_CLASS,
} from '@/core/components/page-header';
import { AutosaveStatusCue } from '@/core/components/ui/autosave-status-cue';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@up2stay/up2promo-ui';
import { useLeadDetail } from '@/core/hooks/use-lead-detail';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  goBackOrFallback,
  useIonBackRouter,
} from '@/core/lib/ion-back-navigation';
import { resolveEventDisplayName } from '@/core/lib/lead-event-names';
import { shellPaths } from '@/core/router/app-paths';
import { isShellObjectIdParam } from '@/core/router/shell-object-id-param';
import {
  getApiBusinessErrorCode,
  getApiErrorStatus,
} from '@/core/utils/api-error.utils';
import { AppIonPage } from '@/core/components/app-ion-page';

function LeadDetailBreadcrumb({
  workLabel,
  leadsLabel,
}: {
  workLabel: string;
  leadsLabel: string;
}) {
  return (
    <>
      {workLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {leadsLabel}
    </>
  );
}

function LeadDetailPageContent({ children }: { children: ReactNode }) {
  return (
    <AppPageContent>
      <AppPageLayout className="min-w-0 overflow-x-hidden md:pb-6">
        {children}
      </AppPageLayout>
    </AppPageContent>
  );
}

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const backRouter = useIonBackRouter();
  const isMobile = useIsMobile();
  const goBack = (fallback: string) => goBackOrFallback(backRouter, fallback);

  const handleBack = () => {
    goBack(shellPaths.leads);
  };

  const detail = useLeadDetail({
    leadId: id,
    onDeleted: () => goBack(shellPaths.leads),
  });

  const [contactEditRequestId, setContactEditRequestId] = useState(0);

  const {
    dict,
    locale,
    detailDict,
    statusLabels,
    unknownAuthorLabel,
    currentWorkspace,
    lead,
    isLoading,
    isPending,
    isFetched,
    error,
    refetch,
    autosaveStatus,
    showDeleteDialog,
    setShowDeleteDialog,
    isDeleting,
    isSavingDescription,
    isSavingName,
    isSubmittingComment,
    isSavingCrmShare,
    isSavingContact,
    isSavingEvents,
    isSavingTags,
    firstNameDraft,
    setFirstNameDraft,
    lastNameDraft,
    setLastNameDraft,
    contactForm,
    setContactForm,
    changeContactForm,
    contactEmailError,
    workspaceEvents,
    tagSuggestions,
    eventNameById,
    resolveAuthorDisplayName,
    resolveAuthorInitials,
    assigneeDisplayReadonly,
    canManageCrm,
    canEditEvents,
    canCreateEvents,
    canEditTags,
    eventsEnabled,
    crmWorkspaceId,
    handleContactFieldBlur,
    handleStatusChange,
    handleDescriptionSave,
    handleFirstNameBlur,
    handleLastNameBlur,
    handleAddComment,
    handleUpdateNote,
    handleDeleteNote,
    currentUserId,
    handleDelete,
    handleSetArchived,
    isArchiving,
    handleCrmAssigneeChange,
    handleCrmShareChange,
    handleContactVia,
    handlePropertyCopy,
    handleLeadUpdatedInCache,
    handleEventsAdd,
    handleEventsRemove,
    handleTagAdd,
    handleTagRemove,
  } = detail;

  const timelineLabels = useMemo(
    () =>
      buildLeadDetailTimelineLabels({
        detailDict,
        eventNameById,
      }),
    [detailDict, eventNameById],
  );

  const attachmentLabels = useMemo(
    () =>
      buildLeadDetailAttachmentLabels({
        detailDict,
        locale,
      }),
    [detailDict, locale],
  );

  const breadcrumb = (
    <LeadDetailBreadcrumb
      workLabel={dict.nav.navDomainWork}
      leadsLabel={dict.nav.leads}
    />
  );

  const heroActions = lead ? (
    <div className="flex flex-wrap items-center gap-2">
      {lead.phone?.trim() ? (
        <Button
          variant="outline"
          size="md"
          className="h-9 gap-[7px] px-3.5 text-[13px] font-semibold"
          onClick={() => void handleContactVia(LeadContactChannel.CALL)}
        >
          <Phone className="size-[15px]" aria-hidden />
          {detailDict.call}
        </Button>
      ) : null}
      {lead.email?.trim() ? (
        <Button
          variant="outline"
          size="md"
          className="h-9 gap-[7px] px-3.5 text-[13px] font-semibold"
          onClick={() => void handleContactVia(LeadContactChannel.EMAIL)}
        >
          <Mail className="size-[15px]" aria-hidden />
          {detailDict.email}
        </Button>
      ) : null}
      {lead.phone?.trim() ? (
        <Button
          variant="outline"
          size="md"
          className="h-9 gap-[7px] px-3.5 text-[13px] font-semibold"
          onClick={() => void handleContactVia(LeadContactChannel.SMS)}
        >
          <MessageSquare className="size-[15px]" aria-hidden />
          SMS
        </Button>
      ) : null}
      <LeadSaveToDeviceMenu lead={lead} labels={detailDict.saveToDevice} />
      {canManageCrm ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              mode="icon"
              className={PAGE_HEADER_CHROME_BUTTON_CLASS}
              aria-label={detailDict.edit}
            >
              <Ellipsis className="size-[18px]" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={isArchiving}
              onClick={() => void handleSetArchived(!isLeadArchived(lead))}
            >
              {isLeadArchived(lead) ? (
                <ArchiveRestore className="size-4" aria-hidden />
              ) : (
                <Archive className="size-4" aria-hidden />
              )}
              {isLeadArchived(lead) ? detailDict.unarchive : detailDict.archive}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="size-4" aria-hidden />
              {detailDict.delete}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  ) : null;

  const isResolvingLead =
    !id || isPending || isLoading || (!!id && !!currentWorkspace && !isFetched);

  if (!isResolvingLead && (error || (id && !lead))) {
    const status = getApiErrorStatus(error);
    const businessCode = getApiBusinessErrorCode(error);
    // A malformed id ("abc") gets a 400 from the API; it can never load, so
    // it is "not found" too instead of a retry that never helps (DEV-558).
    const isNotFound =
      !isShellObjectIdParam(id) ||
      status === 404 ||
      businessCode === 'leadNotFound' ||
      !error;
    const loadError = detailDict.loadError;

    return (
      <AppIonPage>
        <LeadDetailPageContent>
          <PageHeader
            title={dict.leads.title}
            breadcrumb={breadcrumb}
            backHref={shellPaths.leads}
          />
          {isNotFound ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-4 p-8 sm:p-12">
                <p className="text-center text-[13.5px] leading-relaxed text-muted-foreground">
                  {detailDict.notFound}
                </p>
                <Button variant="outline" onClick={handleBack}>
                  {detailDict.back}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-5 sm:p-6">
                <ListEmptyState
                  tone="error"
                  icon={<AlertCircle className="h-6 w-6" aria-hidden />}
                  title={loadError.title}
                  description={loadError.description}
                  action={
                    <Button variant="outline" onClick={() => void refetch()}>
                      {loadError.retry}
                    </Button>
                  }
                />
              </CardContent>
            </Card>
          )}
        </LeadDetailPageContent>
      </AppIonPage>
    );
  }

  if (isResolvingLead || !lead) {
    return (
      <AppIonPage>
        <LeadDetailPageContent>
          <PageHeader
            title={dict.leads.title}
            breadcrumb={breadcrumb}
            backHref={shellPaths.leads}
          />
          <div className="flex w-full min-w-0 flex-col gap-4 md:gap-4">
            <Card>
              <CardContent className="flex flex-col gap-4 p-5 sm:p-6">
                <Skeleton className="h-9 w-2/3" />
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
            <div className="grid w-full min-w-0 grid-cols-1 items-start gap-4 md:gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22.5rem)] lg:gap-4">
              <Card>
                <CardContent className="p-5 sm:p-6">
                  <Skeleton className="h-32 w-full" />
                </CardContent>
              </Card>
              <aside className="hidden min-w-0 lg:block">
                <Card>
                  <CardContent className="flex flex-col gap-3 p-5 sm:p-6">
                    <Skeleton className="h-5 w-28" />
                    <Skeleton className="h-40 w-full" />
                  </CardContent>
                </Card>
              </aside>
            </div>
          </div>
        </LeadDetailPageContent>
      </AppIonPage>
    );
  }

  const propertyLabels = buildLeadDetailPropertyLabels({
    detailDict,
    createDict: dict.leads.create,
  });

  const crmProps = {
    title: detailDict.assignment,
    locale,
    leadOwnerUserId: lead.userId?.trim() ?? '',
    workspaceId: crmWorkspaceId,
    leadsSharingLockedByWorkspace:
      currentWorkspace?.leadsAlwaysSharedWithWorkspace === true,
    creatorLabel: detailDict.crm.creator,
    creatorValue: (() => {
      const createdBy = lead.createdByUserId?.trim();
      if (createdBy) return resolveAuthorDisplayName(createdBy);
      const ownerId = lead.userId?.trim();
      if (!ownerId) return detailDict.crm.creatorPublic;
      const ownerName = resolveAuthorDisplayName(ownerId);
      return ownerName !== unknownAuthorLabel
        ? ownerName
        : detailDict.crm.creatorPublic;
    })(),
    assigneeLabel: detailDict.crm.assignee,
    assigneePlaceholder: detailDict.crm.assigneePlaceholder,
    assigneeTriggerLabel: assigneeDisplayReadonly,
    recordOwnerAssigneeLabel: resolveAuthorDisplayName(lead.userId?.trim()),
    assigneeDisplayReadonly,
    assigneeSearchPlaceholder: detailDict.crm.assigneeSearch,
    assigneeLookupEmpty: detailDict.crm.assigneeNoMembers,
    assigneePickerLabels: leadAssigneePickerLabelsFromDict(
      detailDict.assigneePicker,
    ),
    shareLabel: detailDict.crm.shareWithWorkspace,
    shareReadOnlyHint: detailDict.crm.shareReadOnlyHint,
    sharedOnLabel: detailDict.activitySharingVisible,
    sharedOffLabel: detailDict.activitySharingHidden,
    assigneeUserId: lead.assigneeUserId,
    visibilityIsWorkspace: lead.visibility === 'workspace',
    isPrivate: lead.visibility === 'private',
    editable: canManageCrm,
    isSavingShare: isSavingCrmShare,
    onAssigneeChange: handleCrmAssigneeChange,
    onShareChange: handleCrmShareChange,
  };

  const primaryEventId = lead.eventIds?.[0]?.trim() || null;
  const primaryEventName = primaryEventId
    ? resolveEventDisplayName(primaryEventId, eventNameById, {
        storedName: lead.eventNames?.[primaryEventId],
      })
    : null;

  const consentDisplay = lead.consentAccepted
    ? formatLeadCapturedAt(lead.createdAt, locale) || detailDict.consentYes
    : detailDict.consentNo;

  const tagsSlot = (
    <LeadDetailTags
      lead={lead}
      existingTags={tagSuggestions}
      editable={canEditTags}
      isUpdating={isSavingTags}
      embedded
      labels={{
        title: detailDict.tags,
        empty: detailDict.noTags,
        add: detailDict.addTag,
        remove: detailDict.removeTag,
        placeholder: detailDict.tagPlaceholder,
        apply: detailDict.addTagButton,
        existingTags: detailDict.existingTags,
        noExistingTags: detailDict.noExistingTags,
        readOnlyHint: detailDict.crm.shareReadOnlyHint,
      }}
      onAdd={handleTagAdd}
      onRemove={handleTagRemove}
    />
  );

  const eventsSlot = eventsEnabled ? (
    <LeadDetailEvents
      lead={lead}
      events={workspaceEvents}
      editable={canEditEvents}
      canCreate={canCreateEvents}
      workspaceId={currentWorkspace?.id}
      isUpdating={isSavingEvents}
      embedded
      labels={{
        title: detailDict.eventsTitle,
        empty: detailDict.eventsEmpty,
        add: detailDict.eventsAdd,
        remove: detailDict.eventsRemove,
        noEventsAvailable: detailDict.eventsNoneAvailable,
        updating: detailDict.eventsUpdating,
        create: detailDict.eventsCreate,
        readOnlyHint: detailDict.crm.shareReadOnlyHint,
      }}
      onAdd={handleEventsAdd}
      onRemove={handleEventsRemove}
    />
  ) : (
    <UpgradeCard feature="feature.lead_events" compact />
  );

  const sidebarSharedProps = {
    lead,
    locale,
    labels: {
      contact: detailDict.contact,
      ...propertyLabels,
    },
    assignmentLabels: {
      title: detailDict.assignment,
      visibility: detailDict.visibility,
      private: detailDict.visibilityPrivate,
      workspace: detailDict.visibilityWorkspace,
      hintWorkspace: detailDict.visibilityHintWorkspace,
      hintPrivate: detailDict.visibilityHintPrivate,
    },
    originLabels: {
      title: detailDict.origin,
      source: detailDict.source,
      capturedBy: detailDict.capturedBy,
      event: detailDict.event,
      consent: detailDict.consent,
      consentYes: detailDict.consentYes,
      consentNo: detailDict.consentNo,
    },
    statusLabels,
    sourceLabel: dict.leads.list.source[lead.source] ?? lead.source,
    primaryEventName,
    consentDisplay,
    contactForm,
    isSavingContact,
    onContactFormChange: changeContactForm,
    onContactFieldBlur: (field: ContactFormField) =>
      void handleContactFieldBlur(field),
    contactEmailError,
    onStatusChange: (status: LeadStatus) => void handleStatusChange(status),
    onCopy: handlePropertyCopy,
    crm: crmProps,
    eventsSlot,
    tagsSlot,
  };

  const leadSubtitle = (() => {
    const company = lead.companyName?.trim() ?? '';
    const job = lead.jobTitle?.trim() ?? '';
    if (job && company) return `${job} · ${company}`;
    return leadCompanyLine(lead, true) || null;
  })();

  // Match ReputationSetupPage: AutosaveStatusCue lives in PageHeader actions.
  const autosaveCue = <AutosaveStatusCue status={autosaveStatus} />;

  if (isMobile) {
    return (
      <AppIonPage>
        <LeadDetailPageContent>
          <PageHeader
            title={dict.leads.title}
            hideTitle
            backHref={shellPaths.leads}
            actions={
              <>
                {autosaveCue}
                {canManageCrm ? (
                  <LeadDetailMobileOverflowMenu
                    editContactLabel={detailDict.editContact}
                    archiveLabel={
                      isLeadArchived(lead)
                        ? detailDict.unarchive
                        : detailDict.archive
                    }
                    deleteLabel={detailDict.delete}
                    menuAriaLabel={detailDict.moreActions}
                    archiveDisabled={isArchiving}
                    onEditContact={() =>
                      setContactEditRequestId((prev) => prev + 1)
                    }
                    onArchive={() =>
                      void handleSetArchived(!isLeadArchived(lead))
                    }
                    onDelete={() => setShowDeleteDialog(true)}
                  />
                ) : null}
              </>
            }
          />
          <LeadDetailMobile
            lead={lead}
            locale={locale}
            detailDict={detailDict}
            statusLabels={statusLabels}
            propertyLabels={propertyLabels}
            createDict={dict.leads.create}
            subtitle={leadSubtitle}
            sourceLabel={dict.leads.list.source[lead.source] ?? lead.source}
            primaryEventName={primaryEventName}
            contactForm={contactForm}
            setContactForm={setContactForm}
            crm={crmProps}
            canManageCrm={canManageCrm}
            canEditTags={canEditTags}
            canEditEvents={canEditEvents}
            eventsEnabled={eventsEnabled}
            tagSuggestions={tagSuggestions}
            workspaceEvents={workspaceEvents.map((ev) => ({
              id: ev.id,
              name: ev.name,
            }))}
            eventIds={lead.eventIds ?? []}
            firstNameDraft={firstNameDraft}
            lastNameDraft={lastNameDraft}
            setFirstNameDraft={setFirstNameDraft}
            setLastNameDraft={setLastNameDraft}
            timelineLabels={timelineLabels}
            attachmentLabels={attachmentLabels}
            saveToDeviceLabels={detailDict.saveToDevice}
            tabsLabels={detailDict.tabs}
            descriptionPlaceholder={detailDict.descriptionPlaceholder}
            messageFromLead={lead.message}
            messageLabel={detailDict.messageFromLead}
            isSavingDescription={isSavingDescription}
            isSubmittingComment={isSubmittingComment}
            resolveAuthorDisplayName={resolveAuthorDisplayName}
            resolveAuthorInitials={resolveAuthorInitials}
            onStatusChange={(status) => void handleStatusChange(status)}
            onContactFieldBlur={(field, value) =>
              handleContactFieldBlur(field, value)
            }
            emailInvalidMessage={dict.leads.form.errors.emailInvalid}
            onContactVia={handleContactVia}
            onCopy={handlePropertyCopy}
            onTagAdd={handleTagAdd}
            onTagRemove={handleTagRemove}
            onEventsAdd={handleEventsAdd}
            onEventsRemove={handleEventsRemove}
            onDescriptionSave={(value) => void handleDescriptionSave(value)}
            onSubmitComment={(content) => void handleAddComment(content)}
            currentUserId={currentUserId}
            onUpdateNote={handleUpdateNote}
            onDeleteNote={handleDeleteNote}
            onLeadUpdatedInCache={handleLeadUpdatedInCache}
            onDeleteRequest={() => setShowDeleteDialog(true)}
            onSetArchived={(archived) => void handleSetArchived(archived)}
            onFirstNameBlur={() => void handleFirstNameBlur()}
            onLastNameBlur={() => void handleLastNameBlur()}
            overflowInHeader
            contactEditRequestId={contactEditRequestId}
          />
        </LeadDetailPageContent>

        <LeadDetailDeleteDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          title={detailDict.deleteConfirmTitle}
          description={detailDict.deleteConfirmDescription}
          cancelLabel={dict.settings.cancel}
          confirmLabel={detailDict.deleteConfirm}
          savingLabel={dict.settings.saving}
          isDeleting={isDeleting}
          onConfirm={() => void handleDelete()}
        />
      </AppIonPage>
    );
  }

  return (
    <AppIonPage>
      <LeadDetailPageContent>
        <PageHeader
          title={dict.leads.title}
          breadcrumb={breadcrumb}
          backHref={shellPaths.leads}
          actions={autosaveCue}
        />
        <div
          data-slot="lead-detail-body"
          className="flex w-full min-w-0 flex-col gap-4 md:gap-4"
        >
          {isLeadArchived(lead) ? (
            <Alert variant="warning" className="py-3">
              <Archive className="size-4" aria-hidden />
              <AlertDescription className="text-[13px] text-foreground">
                {detailDict.archivedBanner}
              </AlertDescription>
            </Alert>
          ) : null}
          {/* Full-width hero (LeadDetailDesktop) — CTAs wrap across content width */}
          <LeadDetailSectionCard contentClassName="gap-4">
            <LeadDetailNameTitle
              firstNameDraft={firstNameDraft}
              lastNameDraft={lastNameDraft}
              firstNamePlaceholder={dict.leads.create.firstName}
              lastNamePlaceholder={dict.leads.create.lastName}
              isSavingName={isSavingName}
              status={lead.status}
              statusLabel={statusLabels[lead.status] ?? lead.status}
              initials={leadInitials(lead)}
              subtitle={(() => {
                const company = lead.companyName?.trim() ?? '';
                const job = lead.jobTitle?.trim() ?? '';
                if (job && company) return `${job} · ${company}`;
                return leadCompanyLine(lead, true) || null;
              })()}
              ownershipChip={
                <>
                  {crmWorkspaceId ? (
                    <LeadAssigneePicker
                      workspaceId={crmWorkspaceId}
                      ownerUserId={lead.userId?.trim() ?? ''}
                      ownerOptionLabel={resolveAuthorDisplayName(
                        lead.userId?.trim(),
                      )}
                      assigneeUserId={lead.assigneeUserId ?? undefined}
                      triggerLabel={assigneeDisplayReadonly}
                      triggerClassName="inline-flex h-auto max-w-[16rem] cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[12.5px] font-normal text-foreground shadow-none hover:bg-transparent focus-visible:ring-0"
                      disabled={!crmWorkspaceId}
                      editable={canManageCrm}
                      placeholder={detailDict.crm.assigneePlaceholder}
                      labels={leadAssigneePickerLabelsFromDict(
                        detailDict.assigneePicker,
                      )}
                      locale={locale}
                      isPrivate={lead.visibility === 'private'}
                      workspaceLeadsAlwaysShared={
                        currentWorkspace?.leadsAlwaysSharedWithWorkspace ===
                        true
                      }
                      showSavedCue
                      onAssigneeChange={(next, label) =>
                        handleCrmAssigneeChange(next, label)
                      }
                    />
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                      {assigneeDisplayReadonly ||
                        detailDict.assigneePicker.nobody}
                    </span>
                  )}
                  {crmWorkspaceId ||
                  assigneeDisplayReadonly ||
                  detailDict.assigneePicker?.nobody ? (
                    <span
                      className="hidden h-4 w-px bg-[color:var(--surface-border)] sm:inline-block"
                      aria-hidden
                    />
                  ) : null}
                  <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                    <Users className="size-[13px] shrink-0" aria-hidden />
                    {lead.visibility === 'workspace'
                      ? dict.leads.list.cardMeta.visibilityShared
                      : dict.leads.list.cardMeta.visibilityPrivate}
                  </span>
                </>
              }
              statusControl={
                <LeadStatusSelect
                  lead={lead}
                  statusLabels={statusLabels}
                  onStatusChange={(status) => void handleStatusChange(status)}
                />
              }
              actions={heroActions}
              onFirstNameChange={setFirstNameDraft}
              onLastNameChange={setLastNameDraft}
              onFirstNameBlur={() => void handleFirstNameBlur()}
              onLastNameBlur={() => void handleLastNameBlur()}
            />
          </LeadDetailSectionCard>

          {/*
            Body grid: timeline | sidebar (design ~360px).
            < lg (1024): single column — sidebar stacks below Verlauf.
            < md (768): LeadDetailMobile via useIsMobile.
          */}
          <div className="grid w-full min-w-0 grid-cols-1 items-start gap-4 md:gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22.5rem)] lg:gap-4">
            <LeadDetailSectionCard
              className="min-w-0"
              contentClassName="gap-0 p-0 sm:p-0"
            >
              <Tabs defaultValue="timeline" className="w-full min-w-0">
                <div className="border-b border-[color:var(--surface-border)] px-4 pt-3 sm:px-5">
                  <TabsList className="h-auto w-full justify-start gap-1 rounded-none bg-transparent p-0">
                    <TabsTrigger
                      value="timeline"
                      className="rounded-none border-b-2 border-transparent px-3 pb-2.5 pt-1 text-[13px] data-[state=active]:border-[color:var(--brand-500)] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                    >
                      {detailDict.tabs.timeline}
                      {(lead.activity?.length ?? 0) > 0 ? (
                        <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                          {lead.activity.length}
                        </span>
                      ) : null}
                    </TabsTrigger>
                    <TabsTrigger
                      value="attachments"
                      className="rounded-none border-b-2 border-transparent px-3 pb-2.5 pt-1 text-[13px] data-[state=active]:border-[color:var(--brand-500)] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                    >
                      {detailDict.tabs.attachments}
                      <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                        {lead.attachments?.length ?? 0}
                      </span>
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent
                  value="timeline"
                  className="mt-0 space-y-4 px-4 py-4 sm:px-5"
                >
                  <LeadDetailDescription
                    value={lead.description ?? ''}
                    placeholder={detailDict.descriptionPlaceholder}
                    messageFromLead={lead.message}
                    messageLabel={detailDict.messageFromLead}
                    isSaving={isSavingDescription}
                    onSave={(value) => void handleDescriptionSave(value)}
                  />
                  <LeadDetailTimeline
                    key={lead.id}
                    lead={lead}
                    locale={locale}
                    labels={timelineLabels}
                    statusLabels={statusLabels}
                    resolveAuthorDisplayName={resolveAuthorDisplayName}
                    resolveAuthorInitials={resolveAuthorInitials}
                    isSubmittingComment={isSubmittingComment}
                    onSubmitComment={(content) =>
                      void handleAddComment(content)
                    }
                    currentUserId={currentUserId}
                    noteActionLabels={detailDict.noteActions}
                    onUpdateNote={handleUpdateNote}
                    onDeleteNote={handleDeleteNote}
                    hideTitle
                  />
                </TabsContent>

                <TabsContent
                  value="attachments"
                  className="mt-0 px-4 py-4 sm:px-5"
                >
                  <LeadDetailAttachments
                    lead={lead}
                    labels={attachmentLabels}
                    onUpdated={handleLeadUpdatedInCache}
                  />
                </TabsContent>
              </Tabs>
            </LeadDetailSectionCard>

            <div className="min-w-0 w-full">
              <LeadDetailSidebar variant="desktop" {...sidebarSharedProps} />
            </div>
          </div>
        </div>
      </LeadDetailPageContent>

      <LeadDetailDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title={detailDict.deleteConfirmTitle}
        description={detailDict.deleteConfirmDescription}
        cancelLabel={dict.settings.cancel}
        confirmLabel={detailDict.deleteConfirm}
        savingLabel={dict.settings.saving}
        isDeleting={isDeleting}
        onConfirm={() => void handleDelete()}
      />
    </AppIonPage>
  );
}
